import React, {useState, useEffect} from 'react';
import {Layer, view, SelectField, SelectFieldItem, Icon} from '@sail/ui';
import {FormattedMessage, useIntl} from 'react-intl';
import {info} from '@sail/icons/react/Icon';
import {Text} from 'src/v2/components/common/Text';
import {Button} from 'src/v2/components/common/Button';
import {COLORS} from 'src/v2/util/color';
import {messages} from 'src/v2/components/directDeposit/refundAddressMessages';
import {getNetworkName} from 'src/components/Wallet/constants';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum} from '@stripe-internal/data-v1/schema/types';

type RefundMethod = 'sendToPaymentAddress' | 'setNewRefundAddress';

type RefundAddressDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onApply: (params: {
    refundMethod: RefundMethod;
    customRefundAddress: string;
  }) => Promise<void>;
  initialRefundMethod: RefundMethod;
  initialCustomRefundAddress: string;
  selectedNetwork: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
  livemode: boolean;
  loading: boolean;
};

export const RefundAddressDialog = ({
  isOpen,
  onClose,
  onApply,
  initialRefundMethod,
  initialCustomRefundAddress,
  selectedNetwork,
  livemode: _livemode,
  loading,
}: RefundAddressDialogProps) => {
  const intl = useIntl();
  const [localRefundMethod, setLocalRefundMethod] =
    useState<RefundMethod>(initialRefundMethod);
  const [localCustomRefundAddress, setLocalCustomRefundAddress] = useState(
    initialCustomRefundAddress,
  );

  // Reset local state each time the dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalRefundMethod(initialRefundMethod);
      setLocalCustomRefundAddress(initialCustomRefundAddress);
    }
  }, [isOpen, initialRefundMethod, initialCustomRefundAddress]);

  if (!isOpen) {
    return null;
  }

  const handleApply = async () => {
    await onApply({
      refundMethod: localRefundMethod,
      customRefundAddress: localCustomRefundAddress,
    });
  };

  return (
    <Layer name="refund-address-dialog">
      {/* Backdrop */}
      <view.div
        onClick={onClose}
        css={{
          position: 'fixed',
          inset: 'space.0',
          backgroundColor: 'rgba(0, 0, 0, 0.60)',
          zIndex: 1,
        }}
      />

      {/* Card */}
      <view.div
        css={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
          width: '360px',
          backgroundColor: COLORS.defaultBackgroundColor,
          borderRadius: 'medium',
          padding: 'large',
          stack: 'y',
          gap: 'medium',
          boxShadow:
            '0px 8px 24px rgba(0, 0, 0, 0.12), 0px 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Title */}
        <Text type="title">
          <FormattedMessage {...messages.preferredRefundMethod} />
        </Text>

        {/* Refund Method Select */}
        <SelectField
          label={
            <Text type="input">
              <FormattedMessage {...messages.refundMethodLabel} />
            </Text>
          }
          selectedKey={localRefundMethod}
          onSelectionChange={(key) => setLocalRefundMethod(key as RefundMethod)}
        >
          <SelectFieldItem id="sendToPaymentAddress">
            <Text type="body">
              <FormattedMessage {...messages.sendToPaymentAddress} />
            </Text>
          </SelectFieldItem>
          <SelectFieldItem id="setNewRefundAddress">
            <Text type="body">
              <FormattedMessage {...messages.setNewRefundAddress} />
            </Text>
          </SelectFieldItem>
        </SelectField>

        {/* Custom refund address input */}
        {localRefundMethod === 'setNewRefundAddress' ? (
          <view.div
            css={{display: 'flex', flexDirection: 'column', gap: 'xsmall'}}
          >
            <Text type="input" css={{color: COLORS.textSubduedOnLight}}>
              <FormattedMessage {...messages.newRefundAddressLabel} />
            </Text>

            <view.div
              css={{
                position: 'relative',
                background: COLORS.defaultBackgroundColor,
                boxShadow:
                  '0px 2px 4px rgba(0, 0, 0, 0.07), 0px 1px 1.5px rgba(0, 0, 0, 0.05), 0px 0px 0px 1px rgba(0, 0, 0, 0.08)',
                borderRadius: 'small',
                display: 'flex',
                alignItems: 'center',
                padding: 'small',
              }}
            >
              <view.input
                type="text"
                value={localCustomRefundAddress}
                onChange={(e) => setLocalCustomRefundAddress(e.target.value)}
                placeholder={intl.formatMessage(
                  messages.newRefundAddressPlaceholder,
                )}
                aria-label={intl.formatMessage(
                  messages.newRefundAddressPlaceholder,
                )}
                css={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '14px',
                  color: COLORS.textPrimary,
                }}
              />
            </view.div>

            {/* Network label */}
            <Text
              type="caption"
              css={{
                color: COLORS.textSubduedOnLight,
                marginTop: 'xxsmall',
              }}
            >
              <FormattedMessage
                {...messages.refundNetworkLabel}
                values={{
                  networkName: getNetworkName(selectedNetwork),
                }}
              />
            </Text>
          </view.div>
        ) : null}

        {/* Network mismatch disclaimer - shown only when custom address is selected */}
        {localRefundMethod === 'setNewRefundAddress' ? (
          <view.div
            css={{
              display: 'flex',
              gap: 'small',
              alignItems: 'flex-start',
            }}
          >
            <Icon
              icon={info}
              size="small"
              css={{
                color: COLORS.textSubduedOnLight,
                flexShrink: 0,
                marginTop: 'xxsmall',
              }}
            />
            <Text type="caption" css={{color: COLORS.textSubduedOnLight}}>
              <FormattedMessage {...messages.networkMismatchDisclaimer} />
            </Text>
          </view.div>
        ) : null}

        {/* Apply button */}
        <Button
          onClick={handleApply}
          loading={loading}
          disabled={
            localRefundMethod === 'setNewRefundAddress' &&
            !localCustomRefundAddress.trim()
          }
          css={{
            backgroundColor: COLORS.textPrimary,
          }}
        >
          <FormattedMessage {...messages.apply} />
        </Button>
      </view.div>
    </Layer>
  );
};
